/**
 * QuickWinsCallout — amber callout surfacing CRITICAL/HIGH items with costLow <= $50.
 * PLAN-06 requirement; D-11 from CONTEXT.md.
 * Returns null if no items qualify (empty state is hidden rather than a placeholder).
 */

import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import type { ActionItem } from '../types/homeAudit.types';

type QuickWinsCalloutProps = {
  actionItems: ActionItem[];
};

export const QuickWinsCallout = ({ actionItems }: QuickWinsCalloutProps) => {
  const quickWins = actionItems.filter(
    (item) => (item.priority === 'CRITICAL' || item.priority === 'HIGH') && item.costLow <= 50,
  );
  if (quickWins.length === 0) return null;

  return (
    <Card className="p-6 bg-amber-50 border-amber-300" data-testid="quick-wins-callout">
      <CardContent className="p-0 space-y-4">
        <div>
          <h2 className="text-lg font-medium text-amber-900">Quick Wins</h2>
          <p className="text-sm text-amber-800">High-impact fixes under $50</p>
        </div>
        <ul className="space-y-3">
          {quickWins.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-base font-medium">{item.title}</p>
                  <Badge variant="outline" className="border-amber-400 text-amber-800">
                    {item.isDiy ? 'DIY' : 'Contractor'}
                  </Badge>
                </div>
                <p className="text-sm text-amber-900/80">{item.description}</p>
              </div>
              <p className="text-sm text-amber-900 whitespace-nowrap">
                ${item.costLow} - ${item.costHigh}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
